import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

/* ---- HERO SLIDER DATA ---- */
const slides = [
  {
    tag: 'Premium Signage Solutions',
    heading: <>MAKE YOUR <span>BRAND</span> SHINE</>,
    sub: 'End-to-end signage solutions for businesses across India. LED boards, ACP signs, hoardings and more — built to last.',
    img: 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=1600&q=80',
    cta: { label: 'Explore Services', to: '/services' },
  },
  {
    tag: 'LED & Digital Signage',
    heading: <><span>LED SIGNS</span> THAT DEMAND ATTENTION</>,
    sub: 'Custom LED fabrication with vibrant illumination. Perfect for retail, corporate offices and outdoor installations.',
    img: 'https://images.unsplash.com/photo-1579873948173-b236b5fc5c17?w=1600&q=80',
    cta: { label: 'View Products', to: '/products' },
  },
  {
    tag: 'Outdoor Advertising',
    heading: <>BILLBOARDS & <span>HOARDINGS</span></>,
    sub: 'Large-format outdoor advertising that puts your brand in front of thousands every single day across major Indian cities.',
    img: 'https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=1600&q=80',
    cta: { label: 'Get a Quote', to: '/contact' },
  },
  {
    tag: 'Wayfinding & Interiors',
    heading: <>DESIGN SPACES THAT <span>GUIDE</span></>,
    sub: 'Architectural wayfinding, reception signs, office branding — crafted to enhance every customer touchpoint.',
    img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80',
    cta: { label: 'About Us', to: '/about' },
  },
];

/* ---- SERVICES ---- */
const services = [
  { icon: 'fas fa-lightbulb', title: 'LED Sign Boards', desc: 'Custom illuminated LED letter signs for storefronts, malls and corporate buildings. Energy efficient & long-lasting.' },
  { icon: 'fas fa-layer-group', title: 'ACP Signage', desc: 'Premium aluminium composite panel signs with digital UV printing. Available in hundreds of colour and texture options.' },
  { icon: 'fas fa-map-signs', title: 'Wayfinding Systems', desc: 'Comprehensive directional signage systems for hospitals, campuses, office parks and retail complexes.' },
  { icon: 'fas fa-broadcast-tower', title: 'Hoardings & OOH', desc: 'Large-format outdoor hoardings, unipole structures and gantry boards for high-impact brand visibility.' },
  { icon: 'fas fa-car', title: 'Vehicle Branding', desc: 'Fleet branding and vehicle wraps that turn your transport into moving billboards for maximum reach.' },
  { icon: 'fas fa-desktop', title: 'Digital Displays', desc: 'Indoor and outdoor LED video walls, digital menu boards and interactive display solutions.' },
];

/* ---- PRODUCTS ---- */
const products = [
  {
    tag: 'Illuminated',
    name: 'LED SIGNS',
    desc: 'Three-dimensional stainless steel & acrylic letter signs with LED backlighting.',
    img: 'https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?w=800&q=80',
  },
  {
    tag: 'Exterior',
    name: 'ACP BOARDS',
    desc: 'Premium aluminium composite panel signage with vibrant UV print finishes.',
    img: 'https://images.unsplash.com/photo-1611095564985-4b6e372c3bfb?w=800&q=80',
  },
  {
    tag: 'Wayfinding',
    name: 'TOTEMS & PYLONS',
    desc: 'Tall freestanding totem signs for car parks, campuses and public spaces.',
    img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
  },
  {
    tag: 'Neon',
    name: 'NEON SIGNS',
    desc: 'Custom flex neon and traditional neon tube signs for retro and modern aesthetics.',
    img: 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=800&q=80',
  },
  {
    tag: 'Digital',
    name: 'LED VIDEO WALLS',
    desc: 'High-resolution indoor & outdoor LED screens for dynamic content display.',
    img: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=800&q=80',
  },
  {
    tag: 'Interior',
    name: 'RECEPTION SIGNS',
    desc: 'Bespoke reception area branding with backlit logos, dimensional letters and more.',
    img: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&q=80',
  },
];

/* ---- WHY US ---- */
const whyPoints = [
  { icon: 'fas fa-medal', title: '20+ Years Experience', desc: 'Over two decades of delivering premium signage across India — retail, corporate, hospitality and government sectors.' },
  { icon: 'fas fa-drafting-compass', title: 'In-House Design Studio', desc: 'Our talented designers work closely with you to create custom signage that perfectly represents your brand identity.' },
  { icon: 'fas fa-tools', title: 'End-to-End Execution', desc: 'From site survey and design to fabrication, installation and maintenance — we handle everything under one roof.' },
  { icon: 'fas fa-shield-alt', title: 'Quality Guaranteed', desc: 'Premium materials, precision manufacturing and rigorous QC checks ensure every sign meets our high standards.' },
];

/* ---- TESTIMONIALS ---- */
const testimonials = [
  {
    text: 'Expert Advertising transformed our store frontage completely. The LED signs are stunning and the quality is exceptional. Our foot traffic has noticeably increased.',
    name: 'Rajiv Sharma',
    role: 'Retail Chain Owner, Delhi',
    initials: 'RS',
  },
  {
    text: 'The wayfinding system they installed in our hospital is clear, professional and exactly what we needed. The team was responsive and completed the project on time.',
    name: 'Dr. Priya Mehta',
    role: 'Hospital Administrator',
    initials: 'PM',
  },
  {
    text: 'From concept to installation in under 3 weeks! The ACP board with our logo looks incredibly premium. Highly recommend Expert Advertising for any corporate signage.',
    name: 'Anil Kapoor',
    role: 'Corporate Director, MNC',
    initials: 'AK',
  },
];

/* ---- CLIENTS ---- */
const clients = [
  'MTNL', 'Bank of Baroda', 'ONGC', 'NDMC', 'DLF', 'Tata',
  'Amway', 'Ansal Housing', 'Apollo Hospitals', 'Toshiba',
  'Star Plus', 'Omaxe', 'Educomp', 'Gulshan Homz', 'Birlasoft',
  'MTNL', 'Bank of Baroda', 'ONGC', 'NDMC', 'DLF', 'Tata',
  'Amway', 'Ansal Housing', 'Apollo Hospitals', 'Toshiba',
];

/* ===================================================
   HOME COMPONENT
   =================================================== */
function Home() {
  const [current, setCurrent] = useState(0);
  const [autoplay, setAutoplay] = useState(true);

  const next = useCallback(() => setCurrent(c => (c + 1) % slides.length), []);
  const prev = useCallback(() => setCurrent(c => (c - 1 + slides.length) % slides.length), []);

  useEffect(() => {
    if (!autoplay) return;
    const t = setTimeout(next, 5500);
    return () => clearTimeout(t);
  }, [current, autoplay, next]);

  return (
    <>
      {/* ========== HERO SLIDER ========== */}
      <section className="hero-slider">
        {slides.map((slide, i) => (
          <div key={i} className={`slide ${i === current ? 'active' : ''}`}>
            <img
              src={slide.img}
              alt={`Slide ${i + 1}`}
              className="slide-bg"
              loading={i === 0 ? 'eager' : 'lazy'}
            />
            <div className="slide-overlay" />
            <div className="slide-content">
              <div className="container">
                <div className="slide-tag">{slide.tag}</div>
                <h1 className="slide-heading">{slide.heading}</h1>
                <p className="slide-sub">{slide.sub}</p>
                <div className="slide-btns">
                  <Link to={slide.cta.to} className="btn btn-primary">
                    {slide.cta.label}
                    <i className="fas fa-arrow-right" />
                  </Link>
                  <Link to="/contact" className="btn btn-outline">
                    Get Free Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Arrows */}
        <div className="slider-arrows">
          <button className="slider-arrow" onClick={() => { prev(); setAutoplay(false); }} aria-label="Previous">
            <i className="fas fa-chevron-left" />
          </button>
          <button className="slider-arrow" onClick={() => { next(); setAutoplay(false); }} aria-label="Next">
            <i className="fas fa-chevron-right" />
          </button>
        </div>

        {/* Dots */}
        <div className="slider-controls">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`slider-dot ${i === current ? 'active' : ''}`}
              onClick={() => { setCurrent(i); setAutoplay(false); }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="scroll-indicator">
          <div className="scroll-line" />
          <span>Scroll</span>
        </div>
      </section>

      {/* ========== STATS BAR ========== */}
      <div className="stats-bar">
        <div className="container">
          <div className="stats-grid">
            {[
              { num: '20+', label: 'Years in Business' },
              { num: '2500+', label: 'Projects Delivered' },
              { num: '150+', label: 'Clients Served' },
              { num: '15+', label: 'Cities Covered' },
            ].map((s, i) => (
              <div className="stat-item" key={i}>
                <span className="stat-number">{s.num}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ========== SERVICES ========== */}
      <section className="services-section">
        <div className="container">
          <div className="services-header">
            <div>
              <span className="section-label">What We Do</span>
              <h2 className="section-title">Complete Signage<br />Solutions</h2>
            </div>
            <div>
              <p className="section-desc">
                From conceptualisation to installation, we deliver end-to-end advertising
                and signage solutions tailored to your brand's unique identity.
              </p>
              <Link to="/services" className="btn btn-dark" style={{ marginTop: 24 }}>
                All Services <i className="fas fa-arrow-right" />
              </Link>
            </div>
          </div>
          <div className="services-grid">
            {services.map((svc, i) => (
              <div className="service-card" key={i}>
                <div className="service-icon">
                  <i className={svc.icon} />
                </div>
                <div className="service-title">{svc.title}</div>
                <p className="service-desc">{svc.desc}</p>
                <Link to="/services" className="service-link">
                  Learn More <i className="fas fa-arrow-right" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PRODUCTS ========== */}
      <section className="products-section">
        <div className="container">
          <div className="text-center">
            <span className="section-label">Our Portfolio</span>
            <h2 className="section-title light">Premium Signage<br />Products</h2>
            <p className="section-desc light" style={{ margin: '16px auto 0' }}>
              Discover our wide range of signage products — crafted with precision
              and built to elevate your brand wherever it's seen.
            </p>
          </div>
          <div className="products-grid">
            {products.map((p, i) => (
              <div className="product-card" key={i}>
                <img src={p.img} alt={p.name} loading="lazy" />
                <div className="product-overlay">
                  <div className="product-tag">{p.tag}</div>
                  <div className="product-name">{p.name}</div>
                  <p className="product-desc">{p.desc}</p>
                  <Link to="/products" className="product-arrow">
                    View Details <i className="fas fa-arrow-right" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHY US ========== */}
      <section className="why-section">
        <div className="container">
          <div className="why-grid">
            <div className="why-image-wrap">
              <img
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80"
                alt="Our Workshop"
                className="why-image-main"
                loading="lazy"
              />
              <div className="why-image-badge">
                <span className="badge-number">20</span>
                <span className="badge-text">Years of<br />Excellence</span>
              </div>
            </div>
            <div>
              <span className="section-label">Why Choose Us</span>
              <h2 className="section-title">India's Most Trusted<br />Signage Partner</h2>
              <div className="why-points">
                {whyPoints.map((w, i) => (
                  <div className="why-point" key={i}>
                    <div className="why-point-icon">
                      <i className={w.icon} />
                    </div>
                    <div>
                      <div className="why-point-title">{w.title}</div>
                      <p className="why-point-desc">{w.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/about" className="btn btn-primary" style={{ marginTop: 36 }}>
                Our Story <i className="fas fa-arrow-right" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CTA BANNER ========== */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-inner">
            <div>
              <h2 className="cta-title">
                Ready to Make Your<br /><span>Brand Unforgettable?</span>
              </h2>
              <p className="cta-desc">
                Get a free consultation and quote from our signage experts today.
                Let's create something remarkable together.
              </p>
            </div>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Get Free Quote <i className="fas fa-arrow-right" />
              </Link>
              <a href="tel:+919810012345" className="btn btn-outline">
                <i className="fas fa-phone" /> Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="testimonials-section">
        <div className="container">
          <div className="text-center">
            <span className="section-label">Client Testimonials</span>
            <h2 className="section-title">What Our Clients Say</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((t, i) => (
              <div className="testimonial-card" key={i}>
                <div className="testimonial-quote">"</div>
                <p className="testimonial-text">{t.text}</p>
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, j) => (
                    <i key={j} className="fas fa-star" />
                  ))}
                </div>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{t.initials}</div>
                  <div>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CLIENTS MARQUEE ========== */}
      <section className="clients-section">
        <div className="container">
          <div className="text-center">
            <span className="section-label">Trusted By</span>
            <h2 className="section-title">Our Clients</h2>
          </div>
        </div>
        <div className="clients-marquee-wrap" style={{ marginTop: 48 }}>
          <div className="clients-marquee">
            {clients.map((c, i) => (
              <div className="client-logo-item" key={i}>
                <span className="client-logo-name">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
